import { Component, OnInit, Renderer2, ViewChild, AfterViewInit } from '@angular/core';
import { AppContainerService } from '@mydocma/app-container';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DynamicFormReadyEvent, FieldTypeEnum, FilterSelectFieldConfig } from '@mydocma/dynamic-form';
import { DynamicFormConfig } from '@mydocma/dynamic-form/lib/dynamic-form/dynamic-form.config';
import { FieldsFilterHandlerService } from '../fields-filter-handler.service';
import { FieldsUserFilterService } from '../fields-user-filter.service';
import { FieldsAttributeFilterService } from '../fields-attribute-filter.service';
import { FieldsCompanyCraftOrderService } from '../fields-company-craft-order.service';
import { FieldsDelayFilterService } from '../fields-delay-filter.service';
import { FieldsActivityStatusFilterService } from '../fields-activity-status-filter.service';
import { FieldsResponsibleFilterService } from '../fields-responsible-filter.service';
import { FieldsStatusGroupFilterService } from '../fields-status-group-filter.service';
import { FieldsClassificationFilterService } from '../fields-classification-filter.service';
import { FieldsProjectSpecificFilterService } from '../fields-project-specific-filter.service';
import { FieldsConfigurationService } from '../fields-configuration.service';
import { dateFilterSource } from '../date-filter-config';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { filter } from 'rxjs/operators';
import { FieldsAttributeDropdownTileComponent } from '../fields-attribute-dropdown-tile/fields-attribute-dropdown-tile.component';
import { FieldsDelayDropdownTileComponent } from '../fields-delay-dropdown-tile/fields-delay-dropdown-tile.component';
import { FieldMappingEnum, EMPTY_FILTER_ID } from '../field-mapping.enum';
import { TranslateService } from '@ngx-translate/core';
import { PlatformModeStateService } from '../../../mm-offline/services/platform-mode-state.service';

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-fields-left-pane',
  templateUrl: './fields-left-pane.component.html',
  styleUrls: ['./fields-left-pane.component.scss'],
})
export class FieldsLeftPaneComponent implements OnInit, AfterViewInit {

  @ViewChild('picker') datePicker: MatDateRangePicker<Date>;

  formConfig: DynamicFormConfig;
  showCloseInHeader = true;
  private formReadyEvent: DynamicFormReadyEvent;

  constructor(
    public appContainerService: AppContainerService,
    public filterHandlerService: FieldsFilterHandlerService,
    public userFilterService: FieldsUserFilterService,
    public attributeFilterService: FieldsAttributeFilterService,
    public companyCraftOrderService: FieldsCompanyCraftOrderService,
    public delayFilterService: FieldsDelayFilterService,
    public activityStatusFilterService: FieldsActivityStatusFilterService,
    public responsibleFilterService: FieldsResponsibleFilterService,
    public statusGroupFilterService: FieldsStatusGroupFilterService,
    public classificationFilterService: FieldsClassificationFilterService,
    public projectSpecificFilterService: FieldsProjectSpecificFilterService,
    public fieldsConfigurationService: FieldsConfigurationService,
    private renderer: Renderer2,
    private translateService: TranslateService,
    private readonly platformModeStateService: PlatformModeStateService
  ) {
    this.initializeFormConfig();
  }

  ngOnInit() {
    this.showCloseInHeader = window.innerWidth > 600;

    this.appContainerService.mobileView$
      .pipe(untilDestroyed(this))
      .subscribe(isMobile => this.showCloseInHeader = !isMobile);

    this.fieldsConfigurationService.fieldConfigurations$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateFormConfigurationWithFieldLabels();
      });

    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateEmptyTranslationsInFilters();
      });

    this.platformModeStateService.isLocalMode$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.initializeFormConfig();
        this.updateFormConfigurationWithFieldLabels();
      });
  }


  ngAfterViewInit() {
    if (this.datePicker) {
      this.filterHandlerService.datePickerReady(this.datePicker);
    }
  }

  private initializeFormConfig() {
    this.formConfig = {
      fields: this.createBaseFieldConfigurations(),
      layouts: []
    };
  }

  private createBaseFieldConfigurations(): FilterSelectFieldConfig[] {
    const fieldConfigs: FilterSelectFieldConfig[] = [];

    let fieldTemplates = [
      { id: FieldMappingEnum.CreatedOn, source: dateFilterSource, dropDownPanelClass: 'filter-date', class: 'created-on-dropdown' },
      { id: FieldMappingEnum.Creator, source: [], dropDownPanelClass: 'filter-user', class: 'creator-dropdown' },
      { id: FieldMappingEnum.ModifiedOn, source: dateFilterSource, dropDownPanelClass: 'filter-date', class: 'modified-on-dropdown' },
      { id: FieldMappingEnum.Modifier, source: [], dropDownPanelClass: 'filter-user', class: 'modifier-dropdown' },
      { id: FieldMappingEnum.AttributeGroupImage, source: [], dropDownPanelClass: 'filter-attribute', class: 'attribute-group-dropdown' },
      { id: FieldMappingEnum.AttributeName1, source: [], dropDownPanelClass: 'filter-attribute', class: 'attribute-dropdown' },
      { id: FieldMappingEnum.CompanyName1, source: [], dropDownPanelClass: 'filter-company-craft-order' },
      { id: FieldMappingEnum.CraftName1, source: [], dropDownPanelClass: 'filter-company-craft-order' },
      { id: FieldMappingEnum.OrderName1, source: [], dropDownPanelClass: 'filter-company-craft-order' },
      { id: FieldMappingEnum.ResponsibleCOCompany, source: [], dropDownPanelClass: 'filter-responsible-company', class: 'responsible-co-company-dropdown' },
      { id: FieldMappingEnum.ResponsibleCO, source: [], dropDownPanelClass: 'filter-responsible-contact', class: 'responsible-co-dropdown' },
      { id: FieldMappingEnum.ResponsibleSCCompany, source: [], dropDownPanelClass: 'filter-responsible-company', class: 'responsible-sc-company-dropdown' },
      { id: FieldMappingEnum.ResponsibleSC, source: [], dropDownPanelClass: 'filter-responsible-contact', class: 'responsible-sc-dropdown' },
      { id: FieldMappingEnum.ResponsibleCTCompany, source: [], dropDownPanelClass: 'filter-responsible-company', class: 'responsible-ct-company-dropdown' },
      { id: FieldMappingEnum.ResponsibleCT, source: [], dropDownPanelClass: 'filter-responsible-contact', class: 'responsible-ct-dropdown' },
      { id: FieldMappingEnum.LastActivitySCStatusGroupSC, source: [], dropDownPanelClass: 'filter-status-group', class: 'status-group-sc-dropdown' },
      { id: FieldMappingEnum.LastActivityCOStatusGroupSC, source: [], dropDownPanelClass: 'filter-status-group', class: 'status-group-co-sc-dropdown' },
      { id: FieldMappingEnum.LastActivityCOStatusGroupCT, source: [], dropDownPanelClass: 'filter-status-group', class: 'status-group-co-ct-dropdown' },
      { id: FieldMappingEnum.LastActivityCTStatusGroupCT, source: [], dropDownPanelClass: 'filter-status-group', class: 'status-group-ct-dropdown' },
      { id: FieldMappingEnum.LastActivitySCStatusName1, source: [], dropDownPanelClass: 'filter-activity-status', class: 'activity-sc-status-dropdown' },
      { id: FieldMappingEnum.LastActivityCTStatusName1, source: [], dropDownPanelClass: 'filter-activity-status', class: 'activity-ct-status-dropdown' },
      { id: FieldMappingEnum.DelayGroupSCId, source: [], dropDownPanelClass: 'filter-delay', class: 'delay-group-sc-dropdown' },
      { id: FieldMappingEnum.DelayGroupCTId, source: [], dropDownPanelClass: 'filter-delay', class: 'delay-group-ct-dropdown' },
      { id: FieldMappingEnum.Classification1Name1, source: [], dropDownPanelClass: 'filter-classification', class: 'classification1-dropdown' },
      { id: FieldMappingEnum.Classification2Name1, source: [], dropDownPanelClass: 'filter-classification', class: 'classification2-dropdown' },
      { id: FieldMappingEnum.Classification3Name1, source: [], dropDownPanelClass: 'filter-classification', class: 'classification3-dropdown' },
      { id: FieldMappingEnum.Classification4Name1, source: [], dropDownPanelClass: 'filter-classification', class: 'classification4-dropdown' },
      { id: FieldMappingEnum.ProjectSpecificName1, source: [], dropDownPanelClass: 'filter-project-specific', class: 'project-specific1-dropdown' },
      { id: FieldMappingEnum.ProjectSpecific2Name1, source: [], dropDownPanelClass: 'filter-project-specific', class: 'project-specific2-dropdown' }
    ];

    if(this.platformModeStateService.isLocalMode){
        const fieldsToRemove = [
          FieldMappingEnum.ResponsibleCOCompany,
          FieldMappingEnum.ResponsibleSCCompany,
          FieldMappingEnum.ResponsibleCTCompany,
          FieldMappingEnum.LastActivitySCStatusGroupSC,
          FieldMappingEnum.LastActivityCOStatusGroupSC,
          FieldMappingEnum.LastActivityCOStatusGroupCT,
          FieldMappingEnum.LastActivityCTStatusGroupCT,
          FieldMappingEnum.LastActivitySCStatusName1,
          FieldMappingEnum.LastActivityCTStatusName1,
          FieldMappingEnum.DelayGroupSCId,
          FieldMappingEnum.DelayGroupCTId
        ];

        fieldTemplates  = fieldTemplates.filter(
          field => !fieldsToRemove.includes(field.id)
        );
    }

    fieldTemplates.forEach(template => {
      const config: FilterSelectFieldConfig = {
        id: template.id,
        name: template.id,
        label: {
          text: ''
        },
        type: FieldTypeEnum.filterSelect,
        value: null,
        source: template.source,
        dropDownPanelClass: template.dropDownPanelClass,
        class: template.class
      };

      fieldConfigs.push(config);
    });

    return fieldConfigs;
  }

  private updateFormConfigurationWithFieldLabels(): void {
    if (!this.formConfig?.fields) { return; }

    const originalFieldCount = this.formConfig.fields.length;

    const visibleFields = this.fieldsConfigurationService.getVisibleFilterFields();

    const removedFields: string[] = [];
    const updatedFields: string[] = [];

    this.formConfig.fields = this.formConfig.fields.filter(field => {
      const isVisible = visibleFields.includes(field.id);

      if (isVisible) {
        const displayName = this.fieldsConfigurationService.getFieldDisplayName(field.id);
        if (displayName) {
          (field as FilterSelectFieldConfig).label = {
            text: displayName
          };
          updatedFields.push(`${field.id} -> "${displayName}"`);
        }
      } else {
        removedFields.push(field.id);
      }

      return isVisible;
    }) as FilterSelectFieldConfig[];

  }

  onFormReady(event: DynamicFormReadyEvent): void {
    this.formReadyEvent = event;
    this.filterHandlerService.attach(event, this.renderer);

    this.populateUserDropdowns(event);
    this.populateAttributeDropdowns(event);
    this.populateCompanyCraftOrderDropdowns(event);
    this.populateDelayDropdowns(event);
    this.populateActivityStatusDropdowns(event);
    this.populateResponsibleDropdowns(event);
    this.populateStatusGroupDropdowns(event);
    this.populateClassificationDropdowns(event);
    this.populateProjectSpecificDropdowns(event);
  }

  private populateUserDropdowns(event: DynamicFormReadyEvent): void {
    console.log('[FieldsLeftPane] Setting up user dropdown subscriptions...');

    this.userFilterService.filteredCreators$
      .pipe(untilDestroyed(this))
      .subscribe(creators => {
        console.log('Creator list updated, refreshing dropdown with', creators.length, 'users');

        const creatorConfig = event.dynamicFormService.getFieldConfig('creator') as FilterSelectFieldConfig;
        if (creatorConfig) {
          creatorConfig.source = creators;
          event.dynamicFormService.updateFieldConfig(creatorConfig);
        } else {
          console.warn('[FieldsLeftPane] Creator field config not found - field may have been filtered out');
        }
      });

    this.userFilterService.filteredModifiers$
      .pipe(untilDestroyed(this))
      .subscribe(modifiers => {
        console.log('Modifier list updated, refreshing dropdown with', modifiers.length, 'users');

        const modifierConfig = event.dynamicFormService.getFieldConfig('modifier') as FilterSelectFieldConfig;
        if (modifierConfig) {
          modifierConfig.source = modifiers;
          event.dynamicFormService.updateFieldConfig(modifierConfig);
        } else {
          console.warn('[FieldsLeftPane] Modifier field config not found - field may have been filtered out');
        }
      });
  }

  private populateAttributeDropdowns(event: DynamicFormReadyEvent): void {
    this.attributeFilterService.attributeGroups$
      .pipe(untilDestroyed(this))
      .subscribe(groups => {
        const attributeGroupConfig = event.dynamicFormService.getFieldConfig('attributeGroupImage') as FilterSelectFieldConfig;
        if (attributeGroupConfig) {
          console.log('[FieldsLeftPane] Updating attributeGroupImage dropdown with', groups.length, 'groups');
          attributeGroupConfig.source = groups;
          attributeGroupConfig.valueItemComponent = FieldsAttributeDropdownTileComponent;
          attributeGroupConfig.sourceItemComponent = FieldsAttributeDropdownTileComponent;
          event.dynamicFormService.updateFieldConfig(attributeGroupConfig);
        } else {
          console.warn('[FieldsLeftPane] AttributeGroupImage field config not found - field may have been filtered out');
        }
      });

    this.attributeFilterService.attributesWithoutEmpty$
      .pipe(untilDestroyed(this))
      .subscribe(attributes => {
        const attributeConfig = event.dynamicFormService.getFieldConfig('attributeName1') as FilterSelectFieldConfig;
        if (attributeConfig) {
          console.log('[FieldsLeftPane] Updating attributeName1 dropdown with', attributes.length, 'attributes (no empty option)');
          attributeConfig.source = attributes;
          event.dynamicFormService.updateFieldConfig(attributeConfig);
        } else {
          console.warn('[FieldsLeftPane] AttributeName1 field config not found - field may have been filtered out');
        }
      });

  }

  onDatePickerChange(event: any, type: 'start' | 'end'): void {
    this.filterHandlerService.onDatePickerChange(event, type);
  }


  onCloseClick() {
    if (!this.showCloseInHeader) {
      this.filterHandlerService.applyPendingFilters();
    }
    this.appContainerService.closeContentSideNav();
  }

  private populateCompanyCraftOrderDropdowns(event: DynamicFormReadyEvent): void {
    this.companyCraftOrderService.companies$
      .pipe(untilDestroyed(this))
      .subscribe(companies => {
        const companyConfig = event.dynamicFormService.getFieldConfig('companyName1') as FilterSelectFieldConfig;
        if (companyConfig) {
          companyConfig.source = companies;
          event.dynamicFormService.updateFieldConfig(companyConfig);
        }
      });

    this.companyCraftOrderService.crafts$
      .pipe(untilDestroyed(this))
      .subscribe(crafts => {
        const craftConfig = event.dynamicFormService.getFieldConfig('craftName1') as FilterSelectFieldConfig;
        if (craftConfig) {
          craftConfig.source = crafts;
          event.dynamicFormService.updateFieldConfig(craftConfig);
        }
      });

    this.companyCraftOrderService.orders$
      .pipe(untilDestroyed(this))
      .subscribe(orders => {
        const orderConfig = event.dynamicFormService.getFieldConfig('orderName1') as FilterSelectFieldConfig;
        if (orderConfig) {
          orderConfig.source = orders;
          event.dynamicFormService.updateFieldConfig(orderConfig);
        }
      });
  }

  private populateDelayDropdowns(event: DynamicFormReadyEvent): void {
    this.delayFilterService.delays$
      .pipe(untilDestroyed(this))
      .subscribe(delays => {
        const delayGroupSCConfig = event.dynamicFormService.getFieldConfig(FieldMappingEnum.DelayGroupSCId) as FilterSelectFieldConfig;
        if (delayGroupSCConfig) {
          delayGroupSCConfig.source = delays;
          delayGroupSCConfig.valueItemComponent = FieldsDelayDropdownTileComponent;
          delayGroupSCConfig.sourceItemComponent = FieldsDelayDropdownTileComponent;
          event.dynamicFormService.updateFieldConfig(delayGroupSCConfig);
        }

        const delayGroupCTConfig = event.dynamicFormService.getFieldConfig(FieldMappingEnum.DelayGroupCTId) as FilterSelectFieldConfig;
        if (delayGroupCTConfig) {
          delayGroupCTConfig.source = delays;
          delayGroupCTConfig.valueItemComponent = FieldsDelayDropdownTileComponent;
          delayGroupCTConfig.sourceItemComponent = FieldsDelayDropdownTileComponent;
          event.dynamicFormService.updateFieldConfig(delayGroupCTConfig);
        }
      });
  }

  private populateActivityStatusDropdowns(event: DynamicFormReadyEvent): void {
    this.activityStatusFilterService.scStatuses$
      .pipe(untilDestroyed(this))
      .subscribe(statuses => {
        const scStatusConfig = event.dynamicFormService.getFieldConfig(FieldMappingEnum.LastActivitySCStatusName1) as FilterSelectFieldConfig;
        if (scStatusConfig) {
          scStatusConfig.source = statuses;
          event.dynamicFormService.updateFieldConfig(scStatusConfig);
        }
      });

    this.activityStatusFilterService.ctStatuses$
      .pipe(untilDestroyed(this))
      .subscribe(statuses => {
        const ctStatusConfig = event.dynamicFormService.getFieldConfig(FieldMappingEnum.LastActivityCTStatusName1) as FilterSelectFieldConfig;
        if (ctStatusConfig) {
          ctStatusConfig.source = statuses;
          event.dynamicFormService.updateFieldConfig(ctStatusConfig);
        }
      });
  }

  private populateResponsibleDropdowns(event: DynamicFormReadyEvent): void {
    this.responsibleFilterService.companies$
      .pipe(untilDestroyed(this))
      .subscribe(companies => {
        const companyFields = [
          FieldMappingEnum.ResponsibleCOCompany,
          FieldMappingEnum.ResponsibleSCCompany,
          FieldMappingEnum.ResponsibleCTCompany
        ];

        companyFields.forEach(fieldName => {
          const config = event.dynamicFormService.getFieldConfig(fieldName) as FilterSelectFieldConfig;
          if (config) {
            config.source = companies;
            event.dynamicFormService.updateFieldConfig(config);
          }
        });
      });

    this.responsibleFilterService.contacts$
      .pipe(untilDestroyed(this))
      .subscribe(contacts => {
        const contactFields = [
          FieldMappingEnum.ResponsibleCO,
          FieldMappingEnum.ResponsibleSC,
          FieldMappingEnum.ResponsibleCT
        ];

        contactFields.forEach(fieldName => {
          const config = event.dynamicFormService.getFieldConfig(fieldName) as FilterSelectFieldConfig;
          if (config) {
            config.source = contacts;
            event.dynamicFormService.updateFieldConfig(config);
          }
        });
      });
  }

  private populateStatusGroupDropdowns(event: DynamicFormReadyEvent): void {
    this.statusGroupFilterService.scStatusGroups$
      .pipe(untilDestroyed(this))
      .subscribe(scStatusGroups => {
        const scStatusGroupFields = [
          FieldMappingEnum.LastActivitySCStatusGroupSC,
          FieldMappingEnum.LastActivityCOStatusGroupSC
        ];

        scStatusGroupFields.forEach(fieldName => {
          const config = event.dynamicFormService.getFieldConfig(fieldName) as FilterSelectFieldConfig;
          if (config) {
            config.source = scStatusGroups;
            event.dynamicFormService.updateFieldConfig(config);
          }
        });
      });

    this.statusGroupFilterService.ctStatusGroups$
      .pipe(untilDestroyed(this))
      .subscribe(ctStatusGroups => {
        const ctStatusGroupFields = [
          FieldMappingEnum.LastActivityCOStatusGroupCT,
          FieldMappingEnum.LastActivityCTStatusGroupCT
        ];

        ctStatusGroupFields.forEach(fieldName => {
          const config = event.dynamicFormService.getFieldConfig(fieldName) as FilterSelectFieldConfig;
          if (config) {
            config.source = ctStatusGroups;
            event.dynamicFormService.updateFieldConfig(config);
          }
        });
      });
  }

  private populateClassificationDropdowns(event: DynamicFormReadyEvent): void {
    this.classificationFilterService.classification1$
      .pipe(untilDestroyed(this))
      .subscribe(classifications => {
        const config = event.dynamicFormService.getFieldConfig(FieldMappingEnum.Classification1Name1) as FilterSelectFieldConfig;
        if (config) {
          config.source = classifications;
          event.dynamicFormService.updateFieldConfig(config);
        }
      });

    this.classificationFilterService.classification2$
      .pipe(untilDestroyed(this))
      .subscribe(classifications => {
        const config = event.dynamicFormService.getFieldConfig(FieldMappingEnum.Classification2Name1) as FilterSelectFieldConfig;
        if (config) {
          config.source = classifications;
          event.dynamicFormService.updateFieldConfig(config);
        }
      });

    this.classificationFilterService.classification3$
      .pipe(untilDestroyed(this))
      .subscribe(classifications => {
        const config = event.dynamicFormService.getFieldConfig(FieldMappingEnum.Classification3Name1) as FilterSelectFieldConfig;
        if (config) {
          config.source = classifications;
          event.dynamicFormService.updateFieldConfig(config);
        }
      });

    this.classificationFilterService.classification4$
      .pipe(untilDestroyed(this))
      .subscribe(classifications => {
        const config = event.dynamicFormService.getFieldConfig(FieldMappingEnum.Classification4Name1) as FilterSelectFieldConfig;
        if (config) {
          config.source = classifications;
          event.dynamicFormService.updateFieldConfig(config);
        }
      });
  }

  private populateProjectSpecificDropdowns(event: DynamicFormReadyEvent): void {
    this.projectSpecificFilterService.projectSpecific1$
      .pipe(untilDestroyed(this))
      .subscribe(projectSpecifics => {
        const config = event.dynamicFormService.getFieldConfig(FieldMappingEnum.ProjectSpecificName1) as FilterSelectFieldConfig;
        if (config) {
          config.source = projectSpecifics;
          event.dynamicFormService.updateFieldConfig(config);
        }
      });

    this.projectSpecificFilterService.projectSpecific2$
      .pipe(untilDestroyed(this))
      .subscribe(projectSpecifics => {
        const config = event.dynamicFormService.getFieldConfig(FieldMappingEnum.ProjectSpecific2Name1) as FilterSelectFieldConfig;
        if (config) {
          config.source = projectSpecifics;
          event.dynamicFormService.updateFieldConfig(config);
        }
      });
  }

  private updateEmptyTranslationsInFilters(): void {
    if (!this.formReadyEvent?.dynamicFormService) {
      return;
    }

    const emptyText = this.translateService.instant('MM.fields.filter.empty');

    const fieldsToUpdate = [
      'creator', 'modifier', 'attributeGroupImage', 'attributeName1',
      'companyName1', 'craftName1', 'orderName1',
      'responsibleCOCompany', 'responsibleCO', 'responsibleSCCompany', 'responsibleSC', 'responsibleCTCompany', 'responsibleCT',
      'lastActivitySCStatusGroupSC', 'lastActivityCOStatusGroupSC', 'lastActivityCOStatusGroupCT', 'lastActivityCTStatusGroupCT',
      'lastActivitySCStatusName1', 'lastActivityCTStatusName1',
      'delayGroupSCId', 'delayGroupCTId',
      'classification1Name1', 'classification2Name1', 'classification3Name1', 'classification4Name1',
      'projectSpecificName1', 'projectSpecific2Name1'
    ];

    fieldsToUpdate.forEach(fieldName => {
      const config = this.formReadyEvent.dynamicFormService.getFieldConfig(fieldName) as FilterSelectFieldConfig;
      if (config && config.source && Array.isArray(config.source)) {
        const emptyOption = config.source.find(option => option.id === EMPTY_FILTER_ID);
        if (emptyOption) {
          emptyOption.text = emptyText;
          this.formReadyEvent.dynamicFormService.updateFieldConfig(config);
        }
      }
    });
  }
}
